// Copyright 2023 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { ActivatedFeatureLicenseDTO, LicenseBundle, LicenseBundleKind, LicenseKind } from "datatypes/SoramaWebApi";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import { Api } from "libraries/WebApi/Api";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { Repository } from "repositories/Repository";
import { PackageFileInfo } from "services/Hardware/CamHTTP/ICamApi";

@autoinject
export class LicenseBundleRepository extends Repository {
    constructor(
        private readonly webRequestHandler: IWebRequestHandler,
        private readonly authentication: IAuthentication
    ) {
        super();
    }

    public getLicenseBundleKind(licenseBundleKindId: string): Promise<LicenseBundleKind> {
        return this.webRequestHandler
            .createGetRequest<LicenseBundleKind>(`${Api.licenseBundleKind}/${licenseBundleKindId}`)
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public getOwnedLicenseBundles(licenseHolderId?: string, deviceId?: string): Promise<LicenseBundle[]> {
        if (!licenseHolderId) {
            licenseHolderId = this.authentication.businessUnitId;
        }

        return this.webRequestHandler
            .createGetRequest<LicenseBundle[]>(
                `${Api.licenseBundle}?licenseHolderId=${licenseHolderId}${deviceId != null ? `&deviceId=${deviceId}` : ""}`
            )
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public getActiveBundlesLicenseKinds(licenseHolderId?: string, deviceId?: string): Promise<LicenseKind[]> {
        if (!licenseHolderId) {
            licenseHolderId = this.authentication.businessUnitId;
        }

        return this.webRequestHandler
            .createGetRequest<LicenseKind[]>(
                `${Api.licenseBundle}/Licenses?licenseHolderId=${licenseHolderId}&deviceId=${deviceId}`
            )
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public async activateBundleFeature(
        bundleId: string,
        licenseKindTag: string,
        activatedBundleLicense: ActivatedFeatureLicenseDTO
    ): Promise<string> {
        // Make web request and strip of http response message when returning an error
        return this.webRequestHandler
            .createPutRequest<string>(
                `${Api.licenseBundle}/${bundleId}/Feature/${licenseKindTag}/Activate`,
                activatedBundleLicense
            )
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    public async getActivatableBundleFeaturesForDevice(bundleId: string, deviceId?: string): Promise<LicenseKind[]> {
        return this.webRequestHandler
            .createGetRequest<LicenseKind[]>(
                `${Api.licenseBundle}/${bundleId}/ActivatableFeatures${deviceId ? `?deviceId=${deviceId}` : ""}`
            )
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public getSignedLicenseBundle(bundleId: string, hardwareClock: number): Promise<string> {
        // Make web request and strip of http response message when returning an error
        return this.webRequestHandler
            .createGetRequest<string>(Api.licenseBundle + "/" + bundleId + "/Signed?hardwareClock=" + hardwareClock)
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    public getPackageFile(deviceId: string): Promise<PackageFileInfo> {
        return this.webRequestHandler
            .createGetRequest<PackageFileInfo>(Api.licenseBundle + "/Packages" + `?deviceId=${deviceId}`)
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }
}
